import React, { useState, useEffect, useRef } from 'react';
import { Play, Download, Eye, Calendar, Tag, Filter, Grid, List, X, Volume2, VolumeX, Maximize, Minimize } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { galleryAPI } from '../services/api';

const Gallery = () => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCollection, setSelectedCollection] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [galleries, setGalleries] = useState<any[]>([]);
  const [collections, setCollections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGallery, setSelectedGallery] = useState<any>(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [galleryMedia, setGalleryMedia] = useState<any[]>([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [latestMedia, setLatestMedia] = useState<any[]>([]);
  const [loadingLatestMedia, setLoadingLatestMedia] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    fetchGalleries();
    fetchCollections();
    fetchLatestMedia();
  }, [selectedCategory, selectedCollection]);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      const params: any = {};
      
      if (selectedCategory !== 'all') {
        params.type = selectedCategory;
      }
      
      if (selectedCollection !== 'all') {
        params.collection = selectedCollection;
      }
      
      const response = await galleryAPI.getAll(params);
      if (response.records) {
        setGalleries(response.records);
      } else {
        throw new Error("No galleries found");
      }
    } catch (error) {
      console.error('Error fetching galleries:', error);
      // Fallback to mock data if API fails
      const mockGalleries = [
        {
          id: 1,
          title_fr: 'Africa Surf Tour 2024 - Sénégal',
          title_en: 'Africa Surf Tour 2024 - Senegal',
          type: 'photos',
          collection: 'competitions',
          thumbnail_url: 'https://images.pexels.com/photos/1430672/pexels-photo-1430672.jpeg?auto=compress&cs=tinysrgb&w=600',
          media_count: 45,
          created_at: '2024-03-15',
          description_fr: 'Les meilleurs moments de l\'étape sénégalaise du tour',
          description_en: 'The best moments from the Senegalese stage of the tour'
        },
        {
          id: 2,
          title_fr: 'Highlights Taghazout',
          title_en: 'Highlights Taghazout',
          type: 'videos',
          collection: 'spots',
          thumbnail_url: 'https://images.pexels.com/photos/1761279/pexels-photo-1761279.jpeg?auto=compress&cs=tinysrgb&w=600',
          media_count: 12,
          created_at: '2024-06-10',
          description_fr: 'Vidéos des sessions épiques à Taghazout',
          description_en: 'Videos of epic sessions in Taghazout'
        }
      ];
      setGalleries(mockGalleries);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await galleryAPI.getCollections();
      if (response.records) {
        const allCollections = [
          { id: 'all', name_fr: 'Toutes les Collections', name_en: 'All Collections' }
        ];
        
        response.records.forEach((collection: any) => {
          allCollections.push({
            id: collection.collection,
            name_fr: getCollectionName(collection.collection, 'fr'),
            name_en: getCollectionName(collection.collection, 'en')
          });
        });
        
        setCollections(allCollections);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      // Fallback collections
      setCollections([
        { id: 'all', name_fr: 'Toutes les Collections', name_en: 'All Collections', count: 6 },
        { id: 'competitions', name_fr: 'Compétitions', name_en: 'Competitions', count: 1 },
        { id: 'spots', name_fr: 'Spots de Surf', name_en: 'Surf Spots', count: 2 },
        { id: 'athletes', name_fr: 'Athlètes', name_en: 'Athletes', count: 1 },
        { id: 'documentaries', name_fr: 'Documentaires', name_en: 'Documentaries', count: 1 },
        { id: 'training', name_fr: 'Formation', name_en: 'Training', count: 1 }
      ]);
    }
  };

  const fetchLatestMedia = async () => {
    try {
      setLoadingLatestMedia(true);
      const response = await galleryAPI.getLatestMedia();
      if (response.records) {
        setLatestMedia(response.records);
      } else {
        throw new Error("No latest media found");
      }
    } catch (error) {
      console.error('Error fetching latest media:', error);
      // Fallback data
      const mockLatestMedia = Array.from({ length: 12 }, (_, i) => ({
        id: 1000 + i,
        gallery_id: i % 2 === 0 ? 1 : 2,
        title_fr: `Média récent ${i + 1}`,
        title_en: `Recent media ${i + 1}`,
        media_url: i % 3 === 0 
          ? `/videos/surf_video_${(i % 3) + 1}.mp4` 
          : `https://images.pexels.com/photos/${1400000 + i * 10000}/pexels-photo-${1400000 + i * 10000}.jpeg?auto=compress&cs=tinysrgb&w=300`,
        media_type: i % 3 === 0 ? 'video' : 'image',
        order_position: i + 1,
        created_at: new Date(Date.now() - i * 86400000).toISOString() // Dates décroissantes
      }));
      setLatestMedia(mockLatestMedia);
    } finally {
      setLoadingLatestMedia(false);
    }
  };

  const getCollectionName = (collectionId: string, lang: string) => {
    const collections: {[key: string]: {fr: string, en: string}} = {
      'competitions': {fr: 'Compétitions', en: 'Competitions'},
      'spots': {fr: 'Spots de Surf', en: 'Surf Spots'},
      'athletes': {fr: 'Athlètes', en: 'Athletes'},
      'documentaries': {fr: 'Documentaires', en: 'Documentaries'},
      'training': {fr: 'Formation', en: 'Training'},
      'events': {fr: 'Événements', en: 'Events'}
    };
    
    return collections[collectionId] ? collections[collectionId][lang as keyof {fr: string, en: string}] : collectionId;
  };

  const categories = [
    { id: 'all', name: t('gallery.filter.all'), count: galleries.length },
    { id: 'photos', name: t('gallery.filter.photos'), count: galleries.filter(g => g.type === 'photos').length },
    { id: 'videos', name: t('gallery.filter.videos'), count: galleries.filter(g => g.type === 'videos').length }
  ];

  const filteredGalleries = galleries.filter(gallery => {
    const categoryMatch = selectedCategory === 'all' || gallery.type === selectedCategory;
    const collectionMatch = selectedCollection === 'all' || gallery.collection === selectedCollection;
    return categoryMatch && collectionMatch;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const openGallery = async (gallery: any) => {
    try {
      setLoadingMedia(true);
      setSelectedGallery(gallery);
      setShowGalleryModal(true);
      
      // Récupérer les médias de la galerie
      const response = await galleryAPI.getGalleryMedia(gallery.id);
      if (response.records && response.records.length > 0) {
        setGalleryMedia(response.records);
      } else {
        // Fallback si aucun média n'est trouvé
        setGalleryMedia([]);
      }
    } catch (error) {
      console.error('Error fetching gallery media:', error);
      // Fallback avec des médias simulés
      const mockMedia = Array.from({ length: gallery.media_count || 6 }, (_, i) => ({
        id: i + 1,
        gallery_id: gallery.id,
        title_fr: `${gallery.type === 'photos' ? 'Photo' : 'Vidéo'} ${i + 1}`,
        title_en: `${gallery.type === 'photos' ? 'Photo' : 'Video'} ${i + 1}`,
        media_url: gallery.type === 'photos' 
          ? `https://images.pexels.com/photos/${1400000 + i * 10000}/pexels-photo-${1400000 + i * 10000}.jpeg?auto=compress&cs=tinysrgb&w=1200`
          : `/videos/surf_video_${(i % 3) + 1}.mp4`,
        media_type: gallery.type === 'photos' ? 'image' : 'video',
        order_position: i + 1
      })).slice(0, Math.min(gallery.media_count || 6, 6));
      
      setGalleryMedia(mockMedia);
    } finally {
      setLoadingMedia(false);
    }
  };

  const closeGallery = () => {
    setSelectedGallery(null);
    setShowGalleryModal(false);
    setGalleryMedia([]);
  };

  const openMediaModal = (mediaIndex: number) => {
    setSelectedMediaIndex(mediaIndex);
    setShowMediaModal(true);
  };

  const closeMediaModal = () => {
    setShowMediaModal(false);
    setSelectedMediaIndex(0);
    setIsFullscreen(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const nextMedia = () => {
    if (galleryMedia && selectedMediaIndex < galleryMedia.length - 1) {
      setSelectedMediaIndex(selectedMediaIndex + 1);
    }
  };

  const prevMedia = () => {
    if (selectedMediaIndex > 0) {
      setSelectedMediaIndex(selectedMediaIndex - 1);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const mediaContainer = document.getElementById('media-container');
      if (mediaContainer) {
        mediaContainer.requestFullscreen().catch(err => {
          console.error(`Erreur lors du passage en plein écran: ${err.message}`);
        });
      }
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const openLatestMedia = (media: any) => {
    // Créer une galerie temporaire pour afficher le média
    const tempGallery = {
      id: media.gallery_id || 999,
      title_fr: media.title_fr || 'Média récent',
      title_en: media.title_en || 'Recent media',
      type: media.media_type === 'image' ? 'photos' : 'videos'
    };
    
    setSelectedGallery(tempGallery);
    setGalleryMedia([media]);
    setSelectedMediaIndex(0);
    setShowMediaModal(true);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-900 via-blue-900 to-teal-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            {t('gallery.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto mb-8">
            {t('gallery.hero.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-lg">
              <div className="text-2xl font-bold text-white">{galleries.length}</div>
              <div className="text-sm text-purple-200">{t('gallery.latest.subtitle')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-purple-600 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                  }`}
                >
                  {category.name} 
                </button>
              ))}
            </div>

            {/* Collection Filter & View Mode */}
            <div className="flex items-center gap-4">
              <select
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {collections.map((collection) => (
                  <option key={collection.id} value={collection.id}>
                    {language === 'fr' ? collection.name_fr : collection.name_en}
                  </option>
                ))}
              </select>

              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                  }`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredGalleries.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Aucune galerie trouvée pour cette sélection.</p>
              <button 
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedCollection('all');
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg"
              >
                Réinitialiser les filtres
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredGalleries.map((gallery) => (
                <div 
                  key={gallery.id} 
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group cursor-pointer"
                  onClick={() => openGallery(gallery)}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={gallery.thumbnail_url}
                      alt={language === 'fr' ? gallery.title_fr : gallery.title_en}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    
                    {/* Type Badge */}
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        gallery.type === 'videos' 
                          ? 'bg-red-500 text-white' 
                          : 'bg-blue-500 text-white'
                      }`}>
                        {gallery.type === 'videos' ? 'Vidéos' : 'Photos'}
                      </span>
                    </div>

                   

                    {/* Play/View Button */}
                    {gallery.type === 'videos' && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <button className="bg-white/20 backdrop-blur-md p-4 rounded-full border border-white/30">
                          <Play className="h-8 w-8 text-white" />
                        </button>
                      </div>
                    )}

                    {/* Title Overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-bold text-lg mb-1">
                        {language === 'fr' ? gallery.title_fr : gallery.title_en}
                      </h3>
                      <div className="flex items-center text-white/80 text-sm">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>{formatDate(gallery.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {language === 'fr' ? gallery.description_fr : gallery.description_en}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-sm">
                        <Tag className="h-3 w-3 inline mr-1" />
                        {language === 'fr' 
                          ? getCollectionName(gallery.collection, 'fr') 
                          : getCollectionName(gallery.collection, 'en')}
                      </span>
                      
                      <div className="flex space-x-2">
                        <button className="text-gray-400 hover:text-purple-600 transition-colors">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-purple-600 transition-colors">
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              {filteredGalleries.map((gallery) => (
                <div 
                  key={gallery.id} 
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden cursor-pointer"
                  onClick={() => openGallery(gallery)}
                >
                  <div className="grid grid-cols-1 md:grid-cols-4">
                    <div className="relative h-48 md:h-32 overflow-hidden">
                      <img 
                        src={gallery.thumbnail_url}
                        alt={language === 'fr' ? gallery.title_fr : gallery.title_en}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          gallery.type === 'videos' 
                            ? 'bg-red-500 text-white' 
                            : 'bg-blue-500 text-white'
                        }`}>
                          {gallery.type === 'videos' ? 'Vidéos' : 'Photos'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="md:col-span-3 p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-xl font-bold text-gray-900">
                          {language === 'fr' ? gallery.title_fr : gallery.title_en}
                        </h3>
                      </div>
                      
                      <p className="text-gray-600 mb-4">
                        {language === 'fr' ? gallery.description_fr : gallery.description_en}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span>{formatDate(gallery.created_at)}</span>
                          </div>
                          <span className="bg-purple-100 text-purple-600 px-2 py-1 rounded text-xs">
                            {language === 'fr' 
                              ? getCollectionName(gallery.collection, 'fr') 
                              : getCollectionName(gallery.collection, 'en')}
                          </span>
                        </div>
                        
                        <div className="flex space-x-2">
                          <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition-colors flex items-center">
                            <Eye className="h-4 w-4 mr-1" />
                            Voir
                          </button>
                          <button className="border border-gray-300 hover:border-gray-400 text-gray-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center">
                            <Download className="h-4 w-4 mr-1" />
                            Télécharger
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Gallery Modal */}
      {showGalleryModal && selectedGallery && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {language === 'fr' ? selectedGallery.title_fr : selectedGallery.title_en}
              </h2>
              <button 
                onClick={closeGallery}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-gray-600 mb-6">
                {language === 'fr' ? selectedGallery.description_fr : selectedGallery.description_en}
              </p>
              
              {loadingMedia ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : galleryMedia.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {galleryMedia.map((media, index) => (
                    <div 
                      key={media.id} 
                      className="relative group cursor-pointer"
                      onClick={() => openMediaModal(index)}
                    >
                      {media.media_type === 'video' ? (
                        <div className="relative h-48 overflow-hidden rounded-lg">
                          <video 
                            src={media.media_url}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                            onMouseOver={(e) => e.currentTarget.play()}
                            onMouseOut={(e) => {
                              e.currentTarget.pause();
                              e.currentTarget.currentTime = 0;
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Play className="h-12 w-12 text-white bg-black/50 rounded-full p-3" />
                          </div>
                        </div>
                      ) : (
                        <img 
                          src={media.media_url}
                          alt={media.title_fr || media.title_en || `Media ${index + 1}`}
                          className="w-full h-48 object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
                        />
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-lg flex items-center justify-center">
                        <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      {media.title_fr || media.title_en ? (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 text-sm">
                          {language === 'fr' ? media.title_fr : media.title_en}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Aucun média trouvé pour cette galerie.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Media Modal (Full Screen) */}
      {showMediaModal && selectedGallery && galleryMedia.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
          <div className="relative w-full h-full flex items-center justify-center p-4">
            <button 
              onClick={closeMediaModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="h-8 w-8" />
            </button>
            
            {selectedMediaIndex > 0 && (
              <button 
                onClick={prevMedia}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
              >
                <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {selectedMediaIndex < galleryMedia.length - 1 && (
              <button 
                onClick={nextMedia}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
              >
                <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            
            <div id="media-container" className="max-w-5xl max-h-full">
              {galleryMedia[selectedMediaIndex].media_type === 'video' ? (
                <div className="relative">
                  <video 
                    ref={videoRef}
                    src={galleryMedia[selectedMediaIndex].media_url}
                    className="max-w-full max-h-[80vh] object-contain"
                    controls
                    autoPlay
                    muted={isMuted}
                  />
                  <div className="absolute bottom-4 left-4 flex space-x-2">
                    <button 
                      onClick={toggleMute}
                      className="bg-black/50 p-2 rounded-full text-white hover:bg-black/70 transition-colors"
                    >
                      {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                    </button>
                    <button 
                      onClick={toggleFullscreen}
                      className="bg-black/50 p-2 rounded-full text-white hover:bg-black/70 transition-colors"
                    >
                      {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              ) : (
                <img 
                  src={galleryMedia[selectedMediaIndex].media_url}
                  alt={galleryMedia[selectedMediaIndex].title_fr || galleryMedia[selectedMediaIndex].title_en || `Media ${selectedMediaIndex + 1}`}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              )}
            </div>
            
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-center">
              <p className="text-lg font-semibold">
                {galleryMedia[selectedMediaIndex].title_fr || galleryMedia[selectedMediaIndex].title_en || `${language === 'fr' ? selectedGallery.title_fr : selectedGallery.title_en} - ${selectedMediaIndex + 1}`}
              </p>
              <p className="text-sm opacity-75">{selectedMediaIndex + 1} / {galleryMedia.length}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;